
import React from 'react';
import { motion } from 'motion/react';
import { Clock, CheckCircle2, AlertCircle, Trash2, Edit3 } from 'lucide-react';
import { Order } from '../types/portfolio';
import { safeFormatTimestamp } from '../lib/accountFormatters';

interface OrdersPanelProps {
  orders: Order[];
  title: string;
  type: 'open' | 'recent';
  onEdit?: (order: Order) => void;
  onDelete?: (order: Order) => void;
}

export function OrdersPanel({ orders, title, type, onEdit, onDelete }: OrdersPanelProps) {
  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-sm">
      <div className="p-4 border-b border-[var(--card-border)] bg-[var(--card-header-bg)] flex items-center justify-between">
        <h3 className="text-sm font-black text-[var(--app-text)] uppercase tracking-tight italic">{title}</h3>
        <span className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest bg-[var(--input-bg)] px-2 py-1 rounded border border-[var(--input-border)]">
          {orders.length} Orders
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[var(--card-border)] bg-[var(--card-header-bg)]/50">
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Symbol</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">Side</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right">Qty</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right">Price</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-center">Status</th>
              <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-right">Time</th>
              {type === 'open' && <th className="p-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-center">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const isBuy = order.side === 'buy';
              const isFilled = order.status === 'filled';
              const isCanceled = order.status === 'canceled';

              return (
                <tr key={order.id} className="border-b border-[var(--card-border)] last:border-0 hover:bg-[var(--input-bg)]/50 transition-colors">
                  <td className="p-4">
                    <span className="font-black text-[var(--app-text)] text-sm tracking-tight">{order.symbol}</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase italic ${
                      isBuy ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600'
                    }`}>
                      {order.side}
                    </span>
                  </td>
                  <td className="p-4 text-right font-mono text-xs font-bold text-[var(--app-text)]">
                    {order.qty || order.notional}
                  </td>
                  <td className="p-4 text-right font-mono text-xs font-bold text-[var(--app-text)]">
                    ${order.limit_price || order.filled_avg_price || 'MKT'}
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      {isFilled ? <CheckCircle2 size={12} className="text-emerald-500" /> : 
                       isCanceled ? <AlertCircle size={12} className="text-rose-500" /> : 
                       <Clock size={12} className="text-amber-500 animate-pulse" />}
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${
                        isFilled ? 'text-emerald-600' : isCanceled ? 'text-rose-600' : 'text-amber-600'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-right text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    {safeFormatTimestamp(order.created_at, { mode: 'time', fallback: '—', invalidFallback: '—' })}
                  </td>
                  {type === 'open' && (
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => onEdit?.(order)}
                          className="p-1.5 text-[var(--muted-text)] hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-all active:scale-95"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button 
                          onClick={() => onDelete?.(order)}
                          className="p-1.5 text-[var(--muted-text)] hover:text-rose-600 hover:bg-rose-50 rounded-md transition-all active:scale-95"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
